import { apiClient } from "./fetch";

/**
 * Comms engine -- per-hospital scheduled WhatsApp messages: reminders and
 * follow-ups fired at a day offset from the appointment date, each with its
 * own wording.
 *
 * The router is mounted on both /api/v1/comms and /api/v2/comms (same as
 * programs), so these relative paths ride apiClient's /api/v1 base.
 * hospital_id lives in the path because that is what the tenant guard reads
 * to block cross-hospital access.
 */

export type CommsKind = "reminder" | "followup" | "ad" | "other";
export type CommsLang = "en" | "hi" | "kn" | "ta" | "te" | "ar" | "mr" | "bn";

export interface CommsSchedule {
  comms_schedule_id: string;
  hospital_id: string;
  enabled: boolean;
  label: string;
  kind: CommsKind;
  audience: "patient";
  /** Signed, relative to booked_date: -5 = five days before, +1 = the day after. */
  days_offset: number;
  /** "HH:MM", 24h, in the hospital's own timezone. */
  at_time: string;
  /** Which Meta-approved shell carries the message. */
  template_key: string;
  /** Per-language wording; 'en' is always present and is the fallback. */
  content: Partial<Record<CommsLang, string>>;
  filters: Record<string, unknown>;
  /** booking.status values the rule applies to (0 = booked). */
  booking_statuses: number[];
  created_at?: string;
  updated_at?: string;
}

export interface ScheduleList {
  enabled: boolean; // hospital_feature_flags.comms_engine
  count: number; // enabled rules
  schedules: CommsSchedule[];
}

export interface CommsTemplate {
  template_key: string;
  category: "utility" | "marketing";
  langs: CommsLang[];
  /** Empty until Meta approves at least one language; a rule can't be enabled before that. */
  approved_langs: CommsLang[];
}

export interface TemplateList {
  enabled: boolean;
  templates: CommsTemplate[];
}

export type ScheduleWrite = Pick<
  CommsSchedule,
  "label" | "days_offset" | "at_time" | "template_key" | "content"
> &
  Partial<Pick<CommsSchedule, "kind" | "booking_statuses" | "filters" | "enabled">> & {
    /** Add the rule even if another already fires at the same offset + time. */
    force?: boolean;
  };

export const getSchedules = (hospitalId: string) =>
  apiClient.get(`/comms/schedules/${hospitalId}`);

export const getTemplates = (hospitalId: string) =>
  apiClient.get(`/comms/templates/${hospitalId}`);

// Writes return 403 when the feature is off for the hospital, and 409 for a
// same-slot clash (retry with force: true) or for enabling a rule whose
// template Meta hasn't approved yet.
export const createSchedule = (hospitalId: string, body: ScheduleWrite) =>
  apiClient.post(`/comms/schedules/${hospitalId}`, body);

export const updateSchedule = (
  hospitalId: string,
  scheduleId: string,
  body: Partial<ScheduleWrite>
) => apiClient.patch(`/comms/schedules/${hospitalId}/${scheduleId}`, body);

// Hard delete: the rule's send history goes with it. Disable to keep history.
export const deleteSchedule = (hospitalId: string, scheduleId: string) =>
  apiClient.delete(`/comms/schedules/${hospitalId}/${scheduleId}`);

/** -5 -> "5 days before", 0 -> "Same day", 1 -> "1 day after" */
export const describeOffset = (n: number) => {
  if (n === 0) return "Same day";
  const abs = Math.abs(n);
  return `${abs} day${abs === 1 ? "" : "s"} ${n < 0 ? "before" : "after"}`;
};

// Same range as the API validator: up to 90 days either side of the appointment.
export const MAX_OFFSET_DAYS = 90;
export const OFFSET_OPTIONS: number[] = Array.from({ length: MAX_OFFSET_DAYS * 2 + 1 }, (_, i) => i - MAX_OFFSET_DAYS);

// Five-minute steps, matching the API validator and the 5-minute sweep tick.
// The sweep itself tolerates any minute (it looks back an hour); the step is
// only so the picker and the cron agree on how soon a rule can fire.
export const TIME_STEP_MINUTES = 5;
export const TIME_OPTIONS: string[] = Array.from({ length: (24 * 60) / TIME_STEP_MINUTES }, (_, i) => {
  const total = i * TIME_STEP_MINUTES;
  const h = String(Math.floor(total / 60)).padStart(2, "0");
  const m = String(total % 60).padStart(2, "0");
  return `${h}:${m}`;
});

/** "17:00" -> "5:00 PM" */
export const formatTime12h = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};
